import { useEffect, useState } from 'react'
import { userContextService } from '../../core/infrastructure/user-context-service'

interface User {
  userId: string
  userDetails: string
  identityProvider: string
  userRoles: string[]
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(() => userContextService.getCurrentUser())
  // Initialize authChecked as true if we have a cached user (offline-first)
  // This prevents blocking the entire UI on network calls
  const [authChecked, setAuthChecked] = useState(() => {
    const cachedUser = localStorage.getItem('auth_user')
    return !!cachedUser // true if we have cached auth, false if completely new user
  })
  const [userDetails, setUserDetails] = useState<any>(null)
  const [userDetailsLoading, setUserDetailsLoading] = useState(false)
  const [userDetailsError, setUserDetailsError] = useState<Error | null>(null)
  const [userDetailsReloadKey, setUserDetailsReloadKey] = useState(0)

  useEffect(() => {
    let mounted = true

    const subscription = userContextService.onAuthStateChanged((nextUser) => {
      if (!mounted) return
      setUser(nextUser)
    })

    // Run auth check in background (non-blocking)
    userContextService
      .init()
      .catch((e) => console.error('[useAuth] init failed:', e))
      .finally(() => {
        if (mounted) setAuthChecked(true)
      })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const loadUserDetails = async () => {
      if (!user) {
        setUserDetails(null)
        setUserDetailsError(null)
        return
      }

      setUserDetailsLoading(true)
      setUserDetailsError(null)
      try {
        const details = await userContextService.getUserDetails()
        if (mounted) setUserDetails(details)
      } catch (e) {
        console.error('[useAuth] getUserDetails failed:', e)
        if (mounted) {
          const err = e instanceof Error ? e : new Error('Failed to load user details')
          setUserDetailsError(err)
        }
      } finally {
        if (mounted) setUserDetailsLoading(false)
      }
    }

    loadUserDetails()

    return () => {
      mounted = false
    }
  }, [user?.userId, userDetailsReloadKey])

  const refreshUserDetails = () => {
    setUserDetailsReloadKey((v) => v + 1)
  }

  const login = () => {
    const returnUrl = window.location.pathname + window.location.search
    // replace() removes the current URL from history so pressing back after
    // the OAuth flow does not land on the Google sign-in page.
    window.location.replace(`/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(returnUrl)}`)
  }

  const clearAuthState = () => {
    localStorage.removeItem('auth_user')
    localStorage.removeItem('user_details')
    caches?.delete('api-cache') // Clear cached API responses to prevent cross-user leakage
  }

  const logout = () => {
    clearAuthState()
    // Redirect to the app home page after logout.
    // Note: the SWA auth gateway may still perform provider sign-out hops,
    // but the final landing page will be '/'.
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/'
  }

  const switchAccount = () => {
    clearAuthState()
    const returnUrl = window.location.pathname + window.location.search
    const loginUrl = `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(returnUrl)}`
    window.location.assign(`/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(loginUrl)}`)
  }

  return {
    user,
    authChecked,
    userDetails,
    userDetailsLoading,
    userDetailsError,
    refreshUserDetails,
    login,
    logout,
    switchAccount
  }
}
